// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The tower, drawn in cross-section.
//
// ── THERE IS NO CAMERA, AND THAT IS THE DESIGN ──────────────────────────────────────────────────
// The brief for this game named "a real pan/pinch camera at 375px" as its hard problem. The answer
// here is to delete the problem: the tower is capped at 24 storeys by 12 columns, and `layout()`
// fits the whole of it into whatever canvas it is given. Every storey is on screen at every viewport
// this game supports, on a phone in portrait and on a desktop, always.
//
// That is worth more than the feature it replaces. There is no zoom level at which a control ends up
// off-screen, no gesture that has to be told apart from a pan, no level-of-detail tier that renders
// differently from the one that was tested — and those are, between them, most of the ways a game in
// this fleet has ever shipped a broken layout. A prior build's 9×9 mode overflowed the phone and
// pushed every control out of reach while its 5×5 mode was flawless; the shape of that bug cannot
// occur here, because there is only one scale and it is computed from the viewport.
//
// The only exception is a viewport so short that a storey would fall below 14px — a phone held in
// landscape — where the plot scrolls vertically inside its own box.

import { carPos, type Car } from './lift';
import { COLORS, SURFACES } from './palette';
import type { Sim } from './sim';
import { CELL_RISER, CELL_STAIR, CELL_UNIT, serves, type Unit } from './tower';
import { COLS, GIVE_UP_TICKS, RISER_W, STOREYS, UNITS, type UnitKind } from './tuning';

export type Overlay = 'lift' | 'mood' | 'money';

export interface Layout {
  /** How many storeys the plot is currently sized for — the tower's height plus headroom to grow. */
  storeys: number;
  storeyH: number;
  colW: number;
  /** Left edge of column 0. */
  x0: number;
  /** Top edge of the highest storey. */
  y0: number;
  spineW: number;
  spineX: number;
  w: number;
  h: number;
  dpr: number;
}

/** Below this the plot scrolls instead of shrinking — a storey any shorter is not a tap target. */
const MIN_STOREY_H = 14;
/** Tall enough that a short tower FILLS the stage rather than sitting under a screenful of sky. */
const MAX_STOREY_H = 46;
const MIN_COL_W = 18;
const MAX_COL_W = 34;
const SPINE_W = 12;
const GROUND_H = 10;

/**
 * The height the canvas needs in order to show all 24 storeys at a legible size. On a phone in
 * portrait this is smaller than the stage and the plot simply fits; on a phone in LANDSCAPE the stage
 * is about 125px tall and 24 storeys cannot be squeezed into it at any size worth tapping, so the
 * canvas is made taller than its box and the box scrolls. That is the one and only camera in this
 * game, it exists on one viewport shape, and it moves in one axis.
 */
export const canvasHeightFor = (stageH: number, storeys = STOREYS): number =>
  Math.max(stageH, storeys * MIN_STOREY_H + GROUND_H + 4);

/**
 * How many storeys to draw: what the tower has, plus room to build a few more, never fewer than ten
 * and never more than the cap.
 *
 * Always sizing for the full 24 left a six-storey tower squatting in the bottom third of the phone
 * under two thirds of empty sky, which reads as a broken layout rather than as headroom. Growing the
 * plot as the building grows keeps the tower filling the screen at every point in a run, and because
 * the scale is recomputed rather than scrolled there is still no camera anywhere in this game.
 */
export const plotStoreys = (built: number): number => clamp(built + 3, 10, STOREYS);

export function layout(w: number, h: number, dpr: number, storeys: number = STOREYS): Layout {
  const spineW = SPINE_W;
  const usableH = h - GROUND_H - 4;
  const storeyH = clamp(Math.floor(usableH / storeys), MIN_STOREY_H, MAX_STOREY_H);
  const colW = clamp(Math.floor((w - spineW - 16) / COLS), MIN_COL_W, MAX_COL_W);
  const towerW = colW * COLS;
  const x0 = spineW + 6 + Math.max(0, (w - spineW - 6 - towerW) / 2);
  const y0 = Math.max(2, h - GROUND_H - storeyH * storeys);
  return { storeys, storeyH, colW, x0, y0, spineW, spineX: 2, w, h, dpr };
}

/** The y of the TOP of a storey. Storey 0 sits at the bottom, which is where a concourse goes. */
export const yTop = (L: Layout, storey: number): number => L.y0 + (L.storeys - 1 - storey) * L.storeyH;
export const xOf = (L: Layout, col: number): number => L.x0 + col * L.colW;

/** Which cell a point is over, or null. Used for every tap; deliberately not a function of any frame. */
export function cellAt(L: Layout, px: number, py: number): { storey: number; col: number } | null {
  const col = Math.floor((px - L.x0) / L.colW);
  const storey = L.storeys - 1 - Math.floor((py - L.y0) / L.storeyH);
  if (col < 0 || col >= COLS || storey < 0 || storey >= L.storeys) return null;
  return { storey, col };
}

/** True when the point is over the diagnostic spine down the left edge. */
export const onSpine = (L: Layout, px: number): boolean => px < L.spineX + L.spineW + 4;

export function spineStoreyAt(L: Layout, py: number): number {
  return clamp(L.storeys - 1 - Math.floor((py - L.y0) / L.storeyH), 0, L.storeys - 1);
}

export interface DrawState {
  overlay: Overlay;
  /** The tool the player has armed, if any, and where the ghost currently sits. */
  ghost: { kind: UnitKind | 'riser' | 'stair' | null; storey: number; col: number; legal: boolean } | null;
  /** A storey to flash — the tutorial and the ticker both point with it. */
  flash: number;
  flashUntil: number;
  now: number;
  reduced: boolean;
  selected: number | null;
}

export function draw(ctx: CanvasRenderingContext2D, sim: Sim, L: Layout, st: DrawState): void {
  const { w, h } = L;
  ctx.save();
  ctx.scale(L.dpr, L.dpr);
  ctx.clearRect(0, 0, w, h);

  ctx.fillStyle = SURFACES.sky;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = SURFACES.ground;
  ctx.fillRect(0, h - GROUND_H, w, GROUND_H);

  drawSlabs(ctx, sim, L);
  drawRisers(ctx, sim, L);
  drawUnits(ctx, sim, L, st);
  drawCars(ctx, sim, L);
  drawPeople(ctx, sim, L, st);
  drawSpine(ctx, sim, L, st);
  drawGhost(ctx, L, st);
  ctx.restore();
}

function drawSlabs(ctx: CanvasRenderingContext2D, sim: Sim, L: Layout): void {
  const top = Math.min(L.storeys - 1, Math.max(sim.tower.storeys, 6));
  ctx.fillStyle = SURFACES.slab;
  for (let s = 0; s <= top; s++) {
    ctx.fillRect(L.x0, yTop(L, s), L.colW * COLS, L.storeyH - 1);
  }
  ctx.fillStyle = COLORS.label;
  ctx.font = '9px ui-monospace, SFMono-Regular, Menlo, monospace';
  ctx.textBaseline = 'middle';
  for (let s = 0; s <= top; s += 2) {
    ctx.globalAlpha = 0.55;
    ctx.fillText(String(s), L.x0 - 0, yTop(L, s) + L.storeyH / 2);
    ctx.globalAlpha = 1;
  }
}

function drawRisers(ctx: CanvasRenderingContext2D, sim: Sim, L: Layout): void {
  for (const r of sim.tower.risers) {
    const x = xOf(L, r.col);
    const wpx = L.colW * RISER_W;
    for (let s = r.lo; s <= r.hi; s++) {
      const y = yTop(L, s);
      ctx.fillStyle = SURFACES.shaftWell;
      ctx.fillRect(x, y, wpx, L.storeyH - 1);
      // A storey this riser will not open its doors at is drawn dead, so an express setting is a
      // thing you can SEE rather than a thing you have to remember.
      ctx.fillStyle = COLORS.shaft;
      ctx.globalAlpha = serves(r, s) ? 1 : 0.28;
      ctx.fillRect(x, y, 2, L.storeyH - 1);
      ctx.fillRect(x + wpx - 2, y, 2, L.storeyH - 1);
      ctx.globalAlpha = 1;
    }
  }
}

function drawUnits(ctx: CanvasRenderingContext2D, sim: Sim, L: Layout, st: DrawState): void {
  for (const u of sim.tower.units) {
    const x = xOf(L, u.col);
    const y = yTop(L, u.storey);
    const wpx = L.colW * u.w - 2;
    const hpx = L.storeyH - 3;
    ctx.fillStyle = u.let ? tint(u, st.overlay) : COLORS.vacant;
    ctx.globalAlpha = u.let ? 1 : 0.55;
    ctx.fillRect(x + 1, y + 1, wpx, hpx);
    ctx.globalAlpha = 1;

    if (!u.let) {
      // Vacancy is carried by a HATCH, not by a colour. A vacant fill dark enough to be told apart
      // from a leased one cannot also clear 3:1 against the slab it sits on — the window is empty —
      // so the difference is drawn in a channel that contrast cannot eat.
      ctx.strokeStyle = COLORS.vacant;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = -hpx; i < wpx; i += 5) {
        ctx.moveTo(x + 1 + i, y + 1 + hpx);
        ctx.lineTo(x + 1 + i + hpx, y + 1);
      }
      ctx.save();
      ctx.rect(x + 1, y + 1, wpx, hpx);
      ctx.clip();
      ctx.stroke();
      ctx.restore();
    } else {
      drawGlyph(ctx, u.kind, x + 3, y + 2, hpx);
      if (u.sat <= 0.5 || u.notice) {
        // The mood bar sits in the slab gap UNDER the unit, never as a tint of the unit's own fill —
        // its background is therefore the slab, which is a surface the contrast gate can measure.
        ctx.fillStyle = COLORS.unhappy;
        const bw = Math.max(3, wpx * (1 - u.sat));
        ctx.fillRect(x + 1, y + hpx + 1, bw, 2);
      }
    }
    if (st.selected === u.id) {
      ctx.strokeStyle = COLORS.ok;
      ctx.lineWidth = 2;
      ctx.strokeRect(x + 1, y + 1, wpx, hpx);
    }
  }

  for (const s of sim.tower.stairs) {
    ctx.fillStyle = COLORS.stairs;
    for (const storey of [s.storey, s.storey + 1]) {
      const x = xOf(L, s.col);
      const y = yTop(L, storey);
      ctx.fillRect(x + 2, y + 2, L.colW - 5, L.storeyH - 5);
    }
  }
}

/** Colour by what the active overlay is asking about, not always by what the unit is. */
function tint(u: Unit, overlay: Overlay): string {
  if (overlay === 'mood') {
    if (u.notice || u.sat < 0.35) return COLORS.heatBad;
    if (u.sat < 0.55) return COLORS.heatWarn;
    return COLORS.heatGood;
  }
  if (overlay === 'money') {
    if (u.kind === 'shop') return u.footfallToday > 12 ? COLORS.heatGood : COLORS.heatWarn;
    return u.sat > 0.6 ? COLORS.heatGood : COLORS.heatWarn;
  }
  return COLORS[u.kind];
}

const GLYPHS: Record<UnitKind, string> = { office: '▤', flat: '▥', shop: '◆', service: '✦' };

function drawGlyph(ctx: CanvasRenderingContext2D, kind: UnitKind, x: number, y: number, h: number): void {
  // Every unit kind has a glyph as well as a colour, so the four of them are told apart by shape too
  // and a colour-blind player is never relying on hue alone.
  ctx.fillStyle = SURFACES.slab;
  ctx.font = `${Math.min(11, h)}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  ctx.textBaseline = 'top';
  ctx.fillText(GLYPHS[kind], x, y);
}

function drawCars(ctx: CanvasRenderingContext2D, sim: Sim, L: Layout): void {
  for (const c of sim.cars) {
    const r = sim.tower.risers.find((x) => x.id === c.riser);
    if (!r) continue;
    const pos = carPos(c);
    const x = xOf(L, r.col) + 3;
    const y = yTop(L, 0) - pos * L.storeyH + 2;
    const wpx = L.colW * RISER_W - 6;
    const hpx = L.storeyH - 5;
    ctx.fillStyle = COLORS.car;
    ctx.fillRect(x, y, wpx, hpx);
    // How full it is, in dark pips on the light car — legible from across the tower.
    ctx.fillStyle = COLORS.carLoad;
    const pips = Math.min(6, c.riders.length);
    for (let i = 0; i < pips; i++) ctx.fillRect(x + 2 + i * 3, y + 2, 2, Math.max(2, hpx - 4));
    if (c.riders.length > 0) {
      ctx.fillRect(x + 1, y + hpx - 2, Math.max(1, (wpx - 2) * (c.riders.length / c.cap)), 1);
    }
  }
}

function drawPeople(ctx: CanvasRenderingContext2D, sim: Sim, L: Layout, st: DrawState): void {
  const people = sim.liveAgents();
  const r = Math.max(1.5, Math.min(2.5, L.storeyH / 9));
  for (const p of people) {
    if (p.state === 'ride') continue;
    const x = xOf(L, 0) + p.col * L.colW + L.colW / 2;
    const y = yTop(L, p.storey) + L.storeyH - 3;
    const impatient = p.waitTicks > GIVE_UP_TICKS * 0.45;
    ctx.fillStyle = impatient ? COLORS.impatient : COLORS.person;
    if (!st.reduced && impatient) {
      ctx.globalAlpha = 0.6 + 0.4 * Math.sin(st.now / 160);
    }
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }
}

/**
 * The spine: one segment per storey down the left edge, coloured by the worst wait anybody is
 * currently enduring there. It is the whole tower's health in twelve pixels, and it is the thing the
 * tutorial teaches first — because a player who can read it can diagnose the building, and a player
 * who cannot is looking at a screensaver.
 */
function drawSpine(ctx: CanvasRenderingContext2D, sim: Sim, L: Layout, st: DrawState): void {
  const top = Math.min(L.storeys - 1, Math.max(sim.tower.storeys, 6));
  for (let s = 0; s <= top; s++) {
    const p = sim.storeyPressure(s);
    const y = yTop(L, s);
    const hpx = L.storeyH - 2;
    let colour: string = SURFACES.slab;
    let hatch = 0;
    if (p.n > 0) {
      const frac = p.worstTicks / GIVE_UP_TICKS;
      if (frac > 0.55) {
        colour = COLORS.heatBad;
        hatch = 3;
      } else if (frac > 0.28) {
        colour = COLORS.heatWarn;
        hatch = 2;
      } else colour = COLORS.heatGood;
    }
    ctx.fillStyle = colour;
    // Length carries the magnitude and colour carries only "is this storey failing", because three
    // colours that each clear 3:1 on the same dark ground cannot also be 3:1 from each other.
    const len = p.n > 0 ? Math.max(4, Math.min(L.spineW, 4 + p.n * 2)) : 3;
    ctx.fillRect(L.spineX, y + 1, len, hpx);
    if (hatch > 0 && !st.reduced) {
      ctx.fillStyle = SURFACES.sky;
      for (let i = 0; i < hpx; i += hatch + 2) ctx.fillRect(L.spineX, y + 1 + i, len, 1);
    }
    if (st.flash === s && st.now < st.flashUntil) {
      ctx.strokeStyle = COLORS.ok;
      ctx.lineWidth = 2;
      ctx.strokeRect(L.spineX, y + 1, L.spineW, hpx);
      ctx.strokeRect(L.x0, y + 1, L.colW * COLS, hpx);
    }
  }
}

function drawGhost(ctx: CanvasRenderingContext2D, L: Layout, st: DrawState): void {
  const g = st.ghost;
  if (!g || !g.kind) return;
  const w = g.kind === 'riser' ? RISER_W : g.kind === 'stair' ? 1 : UNITS[g.kind].w;
  ctx.strokeStyle = g.legal ? COLORS.ok : COLORS.no;
  ctx.lineWidth = 2;
  if (g.kind === 'riser') {
    const y = yTop(L, g.storey);
    ctx.strokeRect(xOf(L, g.col) + 1, y + 1, L.colW * w - 2, yTop(L, 0) + L.storeyH - y - 2);
  } else if (g.kind === 'stair') {
    const y = yTop(L, g.storey + 1);
    ctx.strokeRect(xOf(L, g.col) + 1, y + 1, L.colW - 2, L.storeyH * 2 - 3);
  } else {
    ctx.strokeRect(xOf(L, g.col) + 1, yTop(L, g.storey) + 1, L.colW * w - 2, L.storeyH - 3);
  }
}

/** Which unit, if any, is under a cell. The renderer owns this because it owns the geometry. */
export function unitUnder(sim: Sim, storey: number, col: number): Unit | null {
  const i = storey * COLS + col;
  if (sim.tower.kind[i] !== CELL_UNIT) return null;
  const id = sim.tower.id[i];
  return sim.tower.units.find((u) => u.id === id) ?? null;
}

export function riserUnder(sim: Sim, storey: number, col: number): number {
  const i = storey * COLS + col;
  return sim.tower.kind[i] === CELL_RISER ? sim.tower.id[i] : -1;
}

export const isStair = (sim: Sim, storey: number, col: number): boolean =>
  sim.tower.kind[storey * COLS + col] === CELL_STAIR;

export const carsOf = (sim: Sim, riserId: number): Car[] => sim.cars.filter((c) => c.riser === riserId);

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
